/**
 * MultiProcessingModalManager
 * Manages multiple ProcessingModals for simultaneous job queues across different workflows
 * Displays full modal for active workflow, stacks minimized modals for others
 */

import React, { useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ProcessingModal } from './ProcessingModal';
import { JobStreamDisplay } from './JobStreamDisplay';
import { JobStreamUiProvider } from './JobStreamUiContext';
import { loadWorkflowLastJobId } from '../../utils/workflowSessionStorage';
import { switchWorkflowSession, updateWorkflowSessionState } from '../../store/reducers';
import { CheckCircle, Maximize2 } from 'lucide-react';
import { useLanguage, getProcessingModalTranslation } from '../../hooks/useLanguage';
import { getQueueNavigationInfo } from '../../utils/modalNavigationUtils';
import { 
  getComprehensiveWorkflowState,
  shouldShowProcessingModal,
} from '../../utils/processingModalUtils';
import { applyJobStreamSessionStatusUpdate } from '../../utils/workflowStreamStatusUtils';

/**
 * Manages multiple processing modals - one per workflow session
 * Only shows full modal for active workflow, stacks minimized modals for others
 */
export const MultiProcessingModalManager = ({
  isProcessing,
  setIsProcessing,
  setStepsCompleted,
  onProcessComplete,
  onCancelProcessing,
}) => {
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();
  const workflowSessions = useSelector(state => state.workflowSessions);
  const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
  const sessionOrder = workflowSessions?.sessionOrder || [];
  
  // Get processing queue from Redux
  const processingQueue = workflowSessions?.processingQueue || [];
  
  // Track which workflow's modal should be shown (when user clicks on minimized div)
  const [selectedWorkflowForModal, setSelectedWorkflowForModal] = React.useState(null);
  
  // CRITICAL: Use ref to track previous calculation to prevent unnecessary recalculations
  const prevCalculationRef = useRef({
    sessionOrderStr: '',
    activeWorkflowSessionId: null,
    processingQueueStr: '',
    currentlyProcessingSessionId: null,
    sessionsHash: '',
    result: []
  });
  
  // CRITICAL: Use workflowSessions.sessions directly in useMemo to avoid creating new object reference This prevents infinite loops from useMemo recalculating on every render
  const processingWorkflows = useMemo(() => {
    const sessions = workflowSessions?.sessions || {};
    
    // Create stable strings for comparison
    const sessionOrderStr = JSON.stringify(sessionOrder);
    const processingQueueStr = JSON.stringify(processingQueue);
    const currentlyProcessingSessionId = workflowSessions?.currentlyProcessingSessionId;
    
    // Create a stable hash of session states to detect actual changes
    const sessionsHash = Object.keys(sessions).map(id => {
      const s = sessions[id];
      return `${id}:${s?.state?.isProcessing}:${s?.state?.stepsCompleted}:${s?.state?.modalClosed}`;
    }).sort().join('|');
    
    // Check if anything actually changed
    const nothingChanged = 
      prevCalculationRef.current.sessionOrderStr === sessionOrderStr &&
      prevCalculationRef.current.activeWorkflowSessionId === activeWorkflowSessionId &&
      prevCalculationRef.current.processingQueueStr === processingQueueStr &&
      prevCalculationRef.current.currentlyProcessingSessionId === currentlyProcessingSessionId &&
      prevCalculationRef.current.sessionsHash === sessionsHash;
    
    if (nothingChanged) {
      // Return previous result if nothing changed
      return prevCalculationRef.current.result;
    }
    
    // Update ref with new values
    prevCalculationRef.current = {
      sessionOrderStr,
      activeWorkflowSessionId,
      processingQueueStr,
      currentlyProcessingSessionId,
      sessionsHash,
      result: [] // Will be set below
    };
    
    // Include inactive workflows that are processing, in queue, or have jobId Active workflow is handled separately
    const result = sessionOrder.filter(sessionId => {
      // Exclude active workflow - it's handled separately
      if (sessionId === activeWorkflowSessionId) {
        return false;
      }
      
      const session = sessions[sessionId];
      if (!session) return false;
      
      const modalClosed = session?.state?.modalClosed || false;
      if (modalClosed) {
        return false;
      }
      
      const workflowStepsCompleted = session?.state?.stepsCompleted || false;
      if (workflowStepsCompleted) {
        return false;
      }
      
      const workflowJobId = loadWorkflowLastJobId(sessionId);
      const workflowIsProcessing = session?.state?.isProcessing || false;
      const isInQueue = processingQueue.includes(sessionId);
      
      if (!workflowJobId && !workflowIsProcessing && !isInQueue) {
        return false;
      }
      
      const hasDocuments = session?.state?.documents && session.state.documents.length > 0;
      const hasExtractedData = session?.state?.extractedData;
      const hasData = hasDocuments || hasExtractedData;
      
      if (isInQueue) {
        // Skip data check for queue items
      } else if (!hasData) {
        return false;
      }
      
      return true;
    });
    
    // Store result in ref for next comparison
    prevCalculationRef.current.result = result;
    
    return result;
    // CRITICAL: Dependencies are checked inside useMemo using refs This prevents infinite loops when creating new workflows The useMemo will run, but will return early if nothing changed eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionOrder, activeWorkflowSessionId, processingQueue, workflowSessions]);
  
  // OPTIMIZED: Use centralized utility to determine if active workflow should show modal
  const activeVisibility = useMemo(() => {
    if (!activeWorkflowSessionId) {
      return { shouldShow: false, reason: 'no_active_workflow' };
    }
    return shouldShowProcessingModal({
      workflowSessionId: activeWorkflowSessionId,
      isActiveWorkflow: true,
      workflowSessions,
      isProcessing,
    });
  }, [activeWorkflowSessionId, workflowSessions, isProcessing]);
  
  const shouldShowActiveModal = activeVisibility.shouldShow;
  
  // Get active session for minimized state
  const sessions = workflowSessions?.sessions || {};
  const activeSession = activeWorkflowSessionId ? sessions[activeWorkflowSessionId] : null;
  
  // Get minimized state for active workflow
  const activeModalMinimized = activeSession?.state?.modalMinimized || false;
  
  // Active workflow modal should show if it should be shown AND not minimized If minimized, it will appear in the minimized stack instead
  const activeProcessingWorkflow = shouldShowActiveModal && !activeModalMinimized ? activeWorkflowSessionId : null;
  
  // Include active workflow in minimized stack if it's minimized and should be shown
  const workflowsForMinimizedStack = useMemo(() => {
    const stack = [...processingWorkflows];
    if (shouldShowActiveModal && activeModalMinimized && activeWorkflowSessionId) {
      stack.unshift(activeWorkflowSessionId); // Add active workflow at the beginning
    }
    return stack;
  }, [processingWorkflows, shouldShowActiveModal, activeModalMinimized, activeWorkflowSessionId]);
  
  // OPTIMIZED: Memoize minimized divs to ensure they update when workflow state changes
  const minimizedDivs = useMemo(() => {
    if (workflowsForMinimizedStack.length === 0) {
      return null;
    }
    
    const processingSteps = [
      getProcessingModalTranslation('addingFiles', isSpanish),
      getProcessingModalTranslation('processingDocuments', isSpanish),
      getProcessingModalTranslation('processingComplete', isSpanish)
    ];
    
    return (
      <div className="max-h-[90vh] overflow-y-auto p-2 fixed left-4 bottom-4 flex flex-col gap-3 z-999" style={{ maxWidth: '20rem' }}>
        {workflowsForMinimizedStack.map((sessionId) => {
          const isActiveWorkflow = sessionId === activeWorkflowSessionId;
          
          // OPTIMIZED: Use centralized utility to get comprehensive workflow state CRITICAL: This reads from workflowSessions, so it will update when hidden streams update state
          const workflowState = getComprehensiveWorkflowState({
            workflowSessionId: sessionId,
            isActiveWorkflow,
            workflowSessions,
            isProcessing: false, // Inactive workflows use their own state
            initialJobId: loadWorkflowLastJobId(sessionId),
          });
          
          if (!workflowState) {
            return null;
          }
          
          // Extract values from comprehensive state
          const {
            workflowName,
            stepsCompleted,
            isActuallyProcessing,
            isWaiting,
            currentStep,
            completedSteps,
            queuePosition,
            totalInQueue,
          } = workflowState;
          
          const handleMaximize = () => {
            if (isActiveWorkflow) {
              // If it's the active workflow, just unminimize it
              dispatch(updateWorkflowSessionState(sessionId, {
                modalMinimized: false,
              }));
            } else {
              // Switch to this workflow and show its modal
              dispatch(switchWorkflowSession(sessionId));
              dispatch(updateWorkflowSessionState(sessionId, {
                modalMinimized: false,
              }));
              setSelectedWorkflowForModal(sessionId);
            }
          };
          
          return (
            <div
              key={sessionId}
              className={`bg-white border-container rounded-2xl max-w-xs cursor-pointer ${
                isActiveWorkflow ? 'ring-1 ring-primary' : ''
              }`}
              onClick={handleMaximize}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col gap-3">
                  {/* Header with workflow name, queue position, and maximize button */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1">
                        <span className="font-semibold text-sm truncate">{workflowName}</span>
                      </div>
                      {/* {queuePosition && totalInQueue > 1 && (
                        <span className="text-xs text-gray-500 font-medium mt-1">
                          {queuePosition} / {totalInQueue}
                        </span>
                      )} */}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMaximize();
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0 ml-2"
                      title={isSpanish ? 'Maximizar' : 'Maximize'}
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Steps displayed vertically - only show if not waiting */}
                  {!isWaiting ? (
                    <div className="flex flex-col gap-4">
                      {processingSteps.map((step, index) => {
                        const isCompleted = completedSteps.includes(index);
                        const isActive = index <= currentStep;

                        if (!isActive) return null;

                        return (
                          <div key={index} className="flex items-center gap-2">
                            {isCompleted ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                            )}
                            <span className={`text-xs ${!isCompleted && index === currentStep ? "text-primary font-medium" : "text-gray-600"}`}>
                              {step}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-row gap-4 items-center py-4 pb-0 min-w-60">
                      <div className="w-8 h-8 mb-2 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-gray-600 text-center">
                        {isSpanish ? 'Esperando en cola...' : 'Waiting in queue...'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [workflowsForMinimizedStack, workflowSessions, activeWorkflowSessionId, isSpanish]);
  
  // Render active workflow's modal (can be full or minimized) Also render modal for selected workflow (when user clicks on minimized div)
  const renderActiveModal = () => {
    // Show modal for active workflow if it should be shown OR show modal for selected workflow (when user clicked on minimized div)
    const workflowToShow = selectedWorkflowForModal || activeProcessingWorkflow;
    
    if (!workflowToShow) return null;
    
    // If showing selected workflow, make sure it's now the active one
    if (selectedWorkflowForModal && selectedWorkflowForModal !== activeWorkflowSessionId) {
      // Workflow was switched, clear selection
      setSelectedWorkflowForModal(null);
      return null;
    }
    
    // Get the jobId for the workflow
    const workflowJobId = loadWorkflowLastJobId(workflowToShow);
    
    // Use isProcessing prop for active workflow, or check state for selected workflow
    const workflowIsProcessing = workflowToShow === activeWorkflowSessionId 
      ? isProcessing 
      : (workflowSessions?.sessions?.[workflowToShow]?.state?.isProcessing || false);
    
    return (
      <ProcessingModal
        isProcessing={workflowIsProcessing}
        setIsProcessing={setIsProcessing}
        setStepsCompleted={setStepsCompleted}
        onProcessComplete={onProcessComplete}
        onCancelProcessing={onCancelProcessing}
        workflowSessionId={workflowToShow}
        forceMinimized={false}
        initialJobId={workflowJobId}
        useExternalJobStream
      />
    );
  };
  
  // Clear selected workflow when active workflow changes
  React.useEffect(() => {
    if (selectedWorkflowForModal && selectedWorkflowForModal === activeWorkflowSessionId) {
      setSelectedWorkflowForModal(null);
    }
  }, [activeWorkflowSessionId, selectedWorkflowForModal]);
  
  // Get all workflows that should show modals (active + inactive) This is kept for potential future use but not currently used in rendering
  const allWorkflowsToShow = useMemo(() => {
    const workflows = [];
    
    // Add active workflow if it should show
    if (activeProcessingWorkflow) {
      workflows.push({ sessionId: activeProcessingWorkflow, isActive: true });
    }
    
    // Add workflows in minimized stack
    workflowsForMinimizedStack.forEach(sessionId => {
      workflows.push({ sessionId, isActive: sessionId === activeWorkflowSessionId });
    });
    
    return workflows;
  }, [activeProcessingWorkflow, workflowsForMinimizedStack, activeWorkflowSessionId]);
  
  // OPTIMIZED: Track previous calculation to avoid unnecessary recalculations
  const prevWorkflowsStreamsRef = useRef({
    hash: '',
    workflows: [], // Array of { sessionId, jobId }
  });
  
  // OPTIMIZED: Get all workflows that need active streams (have jobId or are processing) CRITICAL: Include ALL workflows with jobIds, even if they're the active workflow This ensures hidden streams run for minimized workflows even when they're active
  const workflowsNeedingStreams = useMemo(() => {
    const sessions = workflowSessions?.sessions || {};
    const workflows = [];
    
    // Build hash string for efficient comparison (only includes relevant state)
    const hashParts = [];
    
    // Check all workflows in sessionOrder
    for (const sessionId of sessionOrder) {
      const session = sessions[sessionId];
      if (!session) continue;
      
      const state = session.state || {};
      const workflowIsProcessing = state.isProcessing || false;
      const isInQueue = processingQueue.includes(sessionId);
      const stepsCompleted = state.stepsCompleted || false;
      
      // CRITICAL: Only create streams for workflows that are NOT completed Once completed, we don't need streams anymore (state is already updated) This prevents infinite loops from status updates triggering recalculations
      const workflowJobId = loadWorkflowLastJobId(sessionId);
      
      // Only include workflows with jobIds that are NOT completed Completed workflows don't need streams - their state is already final
      if (workflowJobId && !stepsCompleted) {
        // CRITICAL: Use stable hash that doesn't include isProcessing (which changes from status updates) Only include sessionId, jobId, and stepsCompleted in hash to prevent loops
        hashParts.push(`${sessionId}:${workflowJobId}:${stepsCompleted}`);
        workflows.push({ sessionId, jobId: workflowJobId });
      }
    }
    
    const currentHash = hashParts.join('|');
    
    // Return cached result if nothing changed
    if (prevWorkflowsStreamsRef.current.hash === currentHash) {
      return prevWorkflowsStreamsRef.current.workflows;
    }
    
    // Update cache
    prevWorkflowsStreamsRef.current = {
      hash: currentHash,
      workflows,
    };
    
    return workflows;
  }, [sessionOrder, workflowSessions, activeWorkflowSessionId, processingQueue]);
  
  // OPTIMIZED: Track refs for handlers and dispatch to prevent rerenders
  const statusUpdateHandlersRef = useRef(new Map());
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  
  // OPTIMIZED: Create status update handler with batched updates
  const createStatusUpdateHandler = useCallback((workflowSessionId) => {
    let cached = statusUpdateHandlersRef.current.get(workflowSessionId);
    if (cached) return cached;
    
    const handler = (statusUpdate) => {
      const currentDispatch = dispatchRef.current;
      applyJobStreamSessionStatusUpdate(workflowSessionId, currentDispatch, statusUpdate);
    };
    
    statusUpdateHandlersRef.current.set(workflowSessionId, handler);
    return handler;
  }, []);
  
  // CRITICAL: Create a stable memoized component for hidden streams This prevents streams from being aborted due to remounting
  const HiddenStreamComponent = React.memo(({ sessionId, jobId, onStatusUpdate }) => {
    
    if (!jobId) return null;
    
    return (
      <div
        style={{ display: 'none' }}
        aria-hidden="true"
      >
        <JobStreamDisplay
          jobId={jobId}
          onStatusUpdate={onStatusUpdate}
          hideUi
          publishUi
        />
      </div>
    );
  });
  
  // Track which streams are active to prevent unnecessary recreations
  const activeStreamsRef = useRef(new Set());
  
  // OPTIMIZED: Create stable stream managers - only recreate when workflows/jobIds actually change
  const hiddenStreamManagers = useMemo(() => {
    const currentStreamIds = new Set(workflowsNeedingStreams.map(w => `${w.sessionId}-${w.jobId}`));
    const managers = [];
    
    // Clean up refs for streams that are no longer active
    for (const streamId of activeStreamsRef.current) {
      if (!currentStreamIds.has(streamId)) {
        activeStreamsRef.current.delete(streamId);
      }
    }
    
    // Create stream components - use stable keys based on sessionId and jobId
    for (const { sessionId, jobId } of workflowsNeedingStreams) {
      if (!jobId) continue;
      
      const streamId = `${sessionId}-${jobId}`;
      activeStreamsRef.current.add(streamId);
      
      const handleStatusUpdate = createStatusUpdateHandler(sessionId);
      
      managers.push(
        <HiddenStreamComponent
          key={streamId}
          sessionId={sessionId}
          jobId={jobId}
          onStatusUpdate={handleStatusUpdate}
        />
      );
    }
    
    return managers;
  }, [workflowsNeedingStreams, createStatusUpdateHandler]);
  
  return (
    <JobStreamUiProvider>
      {/* Hidden stream managers - keep streams active for all processing workflows */}
      {hiddenStreamManagers}

      {/* Full modal for active workflow (or selected workflow) */}
      {renderActiveModal()}

      {/* Minimized divs for inactive workflows - displayed in flex container */}
      {minimizedDivs}
    </JobStreamUiProvider>
  );
};

